import {
  type CreateAdminRoleDto,
  type UpdateAdminRoleDto,
  type V2AdminRoleDetailDto,
  type V2AdminRolesPageDto,
  type V2AdminRolesSummaryDto,
  v2AdminRolesControllerCreate,
  v2AdminRolesControllerDetail,
  v2AdminRolesControllerList,
  v2AdminRolesControllerRemove,
  v2AdminRolesControllerSummary,
  v2AdminRolesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminRoleDetailSchema,
  V2AdminRolesPageSchema,
  V2AdminRolesSummarySchema
} from '@idc-forms/schemas/api-v2/admin-roles'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de roles contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`rol_`); los
 * estados y usuarios de la matriz viajan por `sta_`/`usr_`.
 */

/** Referencia de rol para selects/filtros v2: id público (`rol_`) + nombre. */
export type RoleV2LookupOption = { id: string; name: string }

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type RolesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type RolesAdminV2Row = V2AdminRolesPageDto['data'][number]

function toListQuery(query: RolesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const rolesAdminV2Service = {
  /** Roles activos como referencias `{ id, name }` para selects/filtros v2. */
  lookup: async (accessToken: string): Promise<RoleV2LookupOption[]> => {
    const result = await v2AdminRolesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: { 'page[size]': 200, sort: 'name:asc' }
    })
    return assertShape(V2AdminRolesPageSchema, result.data)
      .data.filter((role) => role.active)
      .map((role) => ({ id: role.id, name: role.name }))
  },

  list: async (accessToken: string, query: RolesAdminV2Query): Promise<V2AdminRolesPageDto> => {
    const result = await v2AdminRolesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminRolesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<RolesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminRolesSummaryDto> => {
    const result = await v2AdminRolesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminRolesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminRoleDetailDto> => {
    const result = await v2AdminRolesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminRoleDetailSchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminRoleDto): Promise<V2AdminRoleDetailDto> => {
    const result = await v2AdminRolesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminRoleDto
  ): Promise<V2AdminRoleDetailDto> => {
    const result = await v2AdminRolesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminRolesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
